import { useMemo, useState } from 'react';
import {
  Box, Typography, Stack, Paper, Button, Divider, Alert, Chip,
} from '@mui/material';
import { PrintOutlined, ContentCopyOutlined, CheckCircleOutlined } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useQuery } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import {
  EntryCategoryLabels, type Entry, type SymptomReading,
} from '@lorrainemarwood-care/shared';
import { api } from '../lib/api';
import SymptomChart from '../components/SymptomChart';

export default function Summary() {
  const [from, setFrom] = useState<Dayjs>(dayjs().subtract(13, 'day').startOf('day'));
  const [to, setTo] = useState<Dayjs>(dayjs().endOf('day'));
  const [copied, setCopied] = useState(false);

  const entriesQuery = useQuery({
    queryKey: ['entries', { from: from.toISOString(), to: to.toISOString() }],
    queryFn: () =>
      api.get<Entry[]>(
        `/api/entries?since=${encodeURIComponent(from.toISOString())}&limit=500`,
      ),
  });

  const symptomsQuery = useQuery({
    queryKey: ['symptoms', { from: from.toISOString(), to: to.toISOString() }],
    queryFn: () =>
      api.get<SymptomReading[]>(
        `/api/symptoms?since=${encodeURIComponent(from.toISOString())}&limit=2000`,
      ),
  });

  const entries = useMemo(
    () => (entriesQuery.data ?? []).filter((e) =>
      dayjs(e.occurredAt).isAfter(from) && dayjs(e.occurredAt).isBefore(to),
    ),
    [entriesQuery.data, from, to],
  );

  const readings = useMemo(
    () => (symptomsQuery.data ?? []).filter((r) =>
      dayjs(r.occurredAt).isAfter(from) && dayjs(r.occurredAt).isBefore(to),
    ),
    [symptomsQuery.data, from, to],
  );

  const days = useMemo(() => groupByDay({ entries, readings, from, to }), [entries, readings, from, to]);

  const onPrint = () => window.print();

  const onCopy = async () => {
    const text = formatAsPlainText({ from, to, days });
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box>
      <Stack
        direction="row"
        sx={{ gap: 2, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 3 }}
        className="no-print"
      >
        <Typography variant="h4" sx={{ fontWeight: 600 }}>Summary</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            onClick={onCopy}
            startIcon={copied ? <CheckCircleOutlined color="success" /> : <ContentCopyOutlined />}
            variant="outlined"
          >
            {copied ? 'Copied' : 'Copy as text'}
          </Button>
          <Button onClick={onPrint} startIcon={<PrintOutlined />} variant="contained">
            Print
          </Button>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} className="no-print">
        <DatePicker
          label="From"
          value={from}
          onChange={(v) => v && setFrom(v.startOf('day'))}
          slotProps={{ textField: { size: 'small' } }}
        />
        <DatePicker
          label="To"
          value={to}
          onChange={(v) => v && setTo(v.endOf('day'))}
          slotProps={{ textField: { size: 'small' } }}
        />
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => { setFrom(dayjs().subtract(6, 'day').startOf('day')); setTo(dayjs().endOf('day')); }}>
            7 days
          </Button>
          <Button size="small" onClick={() => { setFrom(dayjs().subtract(13, 'day').startOf('day')); setTo(dayjs().endOf('day')); }}>
            14 days
          </Button>
          <Button size="small" onClick={() => { setFrom(dayjs().subtract(29, 'day').startOf('day')); setTo(dayjs().endOf('day')); }}>
            30 days
          </Button>
        </Stack>
      </Stack>

      <Box className="printable">
        <Box sx={{ mb: 3, display: 'none' }} className="print-only">
          <Typography variant="h4" sx={{ fontWeight: 600 }}>Care summary</Typography>
          <Typography variant="body2" color="text.secondary">
            {from.format('DD MMM YYYY')} – {to.format('DD MMM YYYY')} · Printed {dayjs().format('DD MMM YYYY HH:mm')}
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }} className="no-print">
          <SymptomChart readings={readings} />
        </Box>

        {days.length === 0 && (
          <Alert severity="info">No entries in this date range.</Alert>
        )}

        <Stack spacing={2}>
          {days.map((day) => (
            <Paper key={day.key} variant="outlined" sx={{ p: 2 }} className="day-block">
              <Typography variant="h6" sx={{ mb: 1 }}>
                {day.label}
              </Typography>

              {day.symptomSummary && (
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mb: 1 }}>
                  <Chip size="small" variant="outlined" label={`Breath ${day.symptomSummary.breath}`} />
                  <Chip size="small" variant="outlined" label={`Pain ${day.symptomSummary.pain}`} />
                  <Chip size="small" variant="outlined" label={`Sleep ${day.symptomSummary.sleep}`} />
                  <Chip size="small" variant="outlined" label={`Appetite ${day.symptomSummary.appetite}`} />
                  <Chip size="small" variant="outlined" label={`Mood ${day.symptomSummary.mood}`} />
                  <Chip size="small" variant="outlined" label={`Look ${day.symptomSummary.look}`} />
                </Stack>
              )}

              {day.meds.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="overline" color="text.secondary">Medications</Typography>
                  <Stack spacing={0.25}>
                    {day.meds.map((m) => (
                      <Typography key={m.id} variant="body2">
                        <b>{dayjs(m.occurredAt).format('HH:mm')}</b>
                        {' — '}
                        {m.medName ?? 'med'}
                        {m.medDose ? ` (${m.medDose})` : ''}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          · {m.authorName}
                        </Typography>
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}

              {day.otherEntries.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="overline" color="text.secondary">Notes</Typography>
                  <Stack spacing={0.5}>
                    {day.otherEntries.map((e) => (
                      <Typography key={e.id} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        <b>{dayjs(e.occurredAt).format('HH:mm')}</b>{' '}
                        <Box component="span" sx={{ textTransform: 'uppercase', fontSize: '0.7em', color: 'text.secondary', mr: 0.5 }}>
                          {EntryCategoryLabels[e.category]}
                        </Box>
                        {e.body}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}

              {day.readings.length > 0 && (
                <Box>
                  <Typography variant="overline" color="text.secondary">Symptom readings</Typography>
                  <Stack spacing={0.25}>
                    {day.readings.map((r) => (
                      <Typography key={r.id} variant="body2">
                        <b>{dayjs(r.occurredAt).format('HH:mm')}</b>
                        {' — '}
                        {[
                          r.breathlessness != null ? `breath ${r.breathlessness}` : null,
                          r.pain != null ? `pain ${r.pain}` : null,
                          r.sleep != null ? `sleep ${r.sleep}` : null,
                          r.appetite != null ? `appetite ${r.appetite}` : null,
                          r.mood != null ? `mood ${r.mood}` : null,
                        ].filter(Boolean).join(' · ')}
                        {r.notes ? ` — ${r.notes}` : ''}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}

              {day.meds.length === 0 && day.otherEntries.length === 0 && day.readings.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No entries recorded.
                </Typography>
              )}
              <Divider sx={{ mt: 1.5, display: { xs: 'none', '@media print': { display: 'block' } } }} />
            </Paper>
          ))}
        </Stack>
      </Box>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          body, .MuiBox-root { background: white !important; color: black !important; }
          .MuiPaper-outlined { border: none !important; page-break-inside: avoid; }
          .day-block { break-inside: avoid; }
        }
        .print-only { display: none; }
      `}</style>
    </Box>
  );
}

interface DayBlock {
  key: string;
  label: string;
  meds: Entry[];
  otherEntries: Entry[];
  readings: SymptomReading[];
  symptomSummary: {
    breath: string;
    pain: string;
    sleep: string;
    appetite: string;
    mood: string;
    look: string;
  } | null;
}

function groupByDay({
  entries, readings, from, to,
}: {
  entries: Entry[];
  readings: SymptomReading[];
  from: Dayjs;
  to: Dayjs;
}): DayBlock[] {
  const days: DayBlock[] = [];
  let cursor = to.startOf('day');
  const start = from.startOf('day');
  while (cursor.isAfter(start) || cursor.isSame(start)) {
    const key = cursor.format('YYYY-MM-DD');
    const dayEntries = entries.filter((e) => dayjs(e.occurredAt).format('YYYY-MM-DD') === key);
    const dayReadings = readings.filter((r) => dayjs(r.occurredAt).format('YYYY-MM-DD') === key);
    const meds = dayEntries.filter((e) => e.category === 'med').sort(byOccurredAt);
    const otherEntries = dayEntries.filter((e) => e.category !== 'med').sort(byOccurredAt);

    days.push({
      key,
      label: cursor.format('dddd, DD MMMM YYYY'),
      meds,
      otherEntries,
      readings: [...dayReadings].sort(byOccurredAt),
      symptomSummary: summarise(dayReadings),
    });
    cursor = cursor.subtract(1, 'day');
  }
  return days;
}

function byOccurredAt<T extends { occurredAt: string }>(a: T, b: T): number {
  return new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime();
}

function summarise(readings: SymptomReading[]): DayBlock['symptomSummary'] {
  if (readings.length === 0) return null;
  const agg = (key: keyof Pick<SymptomReading, 'breathlessness' | 'pain' | 'sleep' | 'appetite' | 'mood' | 'appearance'>) => {
    const values = readings.map((r) => r[key]).filter((v): v is number => v != null);
    if (values.length === 0) return '—';
    const min = Math.min(...values);
    const max = Math.max(...values);
    return min === max ? `${min}` : `${min}–${max}`;
  };
  return {
    breath: agg('breathlessness'),
    pain: agg('pain'),
    sleep: agg('sleep'),
    appetite: agg('appetite'),
    mood: agg('mood'),
    look: agg('appearance'),
  };
}

function formatAsPlainText({
  from, to, days,
}: {
  from: Dayjs;
  to: Dayjs;
  days: DayBlock[];
}): string {
  const lines: string[] = [];
  lines.push(`CARE SUMMARY — ${from.format('DD MMM YYYY')} to ${to.format('DD MMM YYYY')}`);
  lines.push('');

  for (const day of days) {
    if (day.meds.length === 0 && day.otherEntries.length === 0 && day.readings.length === 0) continue;

    lines.push(`── ${day.label} ──`);
    if (day.symptomSummary) {
      const s = day.symptomSummary;
      lines.push(`  Symptoms: breath ${s.breath} · pain ${s.pain} · sleep ${s.sleep} · appetite ${s.appetite} · mood ${s.mood} · look ${s.look}`);
    }
    if (day.meds.length > 0) {
      lines.push(`  Medications:`);
      for (const m of day.meds) {
        lines.push(`    ${dayjs(m.occurredAt).format('HH:mm')} — ${m.medName ?? 'med'}${m.medDose ? ` (${m.medDose})` : ''}`);
      }
    }
    if (day.otherEntries.length > 0) {
      lines.push(`  Notes:`);
      for (const e of day.otherEntries) {
        lines.push(`    ${dayjs(e.occurredAt).format('HH:mm')} [${EntryCategoryLabels[e.category]}] ${e.body.replace(/\n/g, ' ')}`);
      }
    }
    if (day.readings.length > 0) {
      lines.push(`  Readings:`);
      for (const r of day.readings) {
        const parts = [
          r.breathlessness != null ? `breath ${r.breathlessness}` : null,
          r.pain != null ? `pain ${r.pain}` : null,
          r.sleep != null ? `sleep ${r.sleep}` : null,
          r.appetite != null ? `appetite ${r.appetite}` : null,
          r.mood != null ? `mood ${r.mood}` : null,
          r.appearance != null ? `look ${r.appearance}` : null,
        ].filter(Boolean).join(' · ');
        lines.push(`    ${dayjs(r.occurredAt).format('HH:mm')} ${parts}${r.notes ? ` — ${r.notes}` : ''}`);
      }
    }
    lines.push('');
  }
  return lines.join('\n');
}
